/*
 * Transform call data into a dialer state description object.
 *
 * This is just a transformation, no local data are needed.
 *
 * Returns a list of currently ongoing calls from the PoV of
 * `exten` that is passed in as an argument. This is used by
 * the dialer to render the active call UI.
 *
 * Properties:
 * - call_id: Call linked ID
 * - status: connected / dialing / hold / disconnected / incomming
 * - peer: number
 * - queue: If the call is comming in from a queue, this is a queue name
 * - start_ts: Start timestamp of the call (whatever time needs to be
 *             shown in the dialer) Null, to hide the time.
 * - transfer:
 *   - status: dialing / connected
 *   - peer: number
 */
function project_call_to_exten(call, exten) {

	/*
	 * When the call starts, it will have multiple connections
	 * between the original caller and all parallel ringing
	 * devices of the callee.
	 *
	 * These will be without connection.start_ts until the
	 * callee picks up the call on one of the devices.
	 *
	 * After that the former ringing connections will disappear.
	 */

	// assume the exten is present only one peer in the call (no multiple presences like both caller and callee)

	const call_connections_sorted = [...call.connections];

	// presort to take the last connection in case the exten is present multiple times in a call (untested edge case)
	// the dialer would show only the last presence in this case (untested)
	// TODO: return multiple exten projections in this case
	call_connections_sorted.sort((c1, c2) => new Date(c1.dial_ts || c1.start_ts).getTime() - new Date(c2.dial_ts || c2.start_ts).getTime());


	/**
	 * the list of active connections for the exten. If there is no connection (for example a call waiting in a
	 * queue before dialing another agent), the list is empty. The channel is non null in this case.
	 * @type {[{conn, my_channel, peer_channel}]}
	 */
	const connections = [];

	let role; // 'caller' | 'callee'

	/**
	 * the peer channel object for the exten
	 *
	 * It might be null for originated call in dialing target phase. In this case, connections.length > 0.
	 *
	 * if the followme is active, the channel.exten might not equal the given exten
	 *
	 * If the call is not connected, there can be multiple connections and multiple channels
	 * (if there are multiple devices ringing for example): all of them should share more or less same data,
	 * so just take the first one.
	 *
	 */
	let channels = [];


	// try to find the exten amongst the peer channels in the connections
	for (const conn of call_connections_sorted) {

		let as_caller = conn.caller == exten ||
			((conn.path && conn.path.caller) || []).some(p => p.exten == exten) ||
			(conn.channel_caller && conn.channel_caller.exten == exten);
			
		let as_callee = conn.callee == exten ||
			((conn.path && conn.path.callee) || []).some(p => p.exten == exten) ||
			(conn.channel_callee && conn.channel_callee.exten == exten);
		
		if (as_caller && as_callee) {
			// one of the peer channel must be local, take the other one
			// this fixes calling to or from the Asterisk (local channel) if the local channel has the same exten
			// for example: originated call, where we are the caller
			if (conn.channel_caller && conn.channel_caller.channel.match(/;[12]$/)) {
				// assume the caller is local channel and the callee is not
				as_caller = false;
			} else {
				as_callee = false;
			}
		}
		
		if (as_caller) {
			// the exten is found as a peer caller channel or as a member of the caller followme path
			role = 'caller';
			if (conn.channel_caller) {
				channels.push(conn.channel_caller);
			}
			connections.push({
				conn,
				my_channel: conn.channel_caller,
				peer_channel: conn.channel_callee
			});
		} 
		if (as_callee) {
			// the exten is found as a peer callee channel or as a member of the callee followme path
			role = 'callee';
			if (conn.channel_callee) {
				channels.push(conn.channel_callee);
			}
			connections.push({
				conn,
				my_channel: conn.channel_callee,
				peer_channel: conn.channel_caller
			});
		}
	}

	// find exten in peers if not found in connections. There can be
	// orphan channels without connection for example calling to
	// queue after ringing all agents and before subsequent agents
	// ringing, or other internal calls from Asterisk to the extension
	if (channels.length == 0) {
		for (const peer of call.peers) {
			if (peer.exten == exten) {
				channels.push(peer);
			}
		}
	}
	if (channels.length == 0) {
		// the exten was not found in the call
		return null;
	}
	channels.sort((a, b) => a.sequence - b.sequence);
	let channel = channels[0]; // handle originated calls in target dialing phase

	// detect originated call in a target dialing phase
	let originate_dialing_caller = false;
	if (role == 'callee' && connections[0] && connections[0].conn.caller == null) {
		// this is an originated call in a caller dialing phase
		// -> reverse the direction to show this as an outgoing call instead of an incoming one
		// this assumes followme is not done to internal line, if it does, this condition must exclude case, when the
		//  asterisk is dialing my webphone and not some HW phone (or other webphone)

		const conn = connections[0];
		[conn.my_channel, conn.peer_channel] = [conn.peer_channel, conn.my_channel];
		role = 'caller';
		originate_dialing_caller = true;
	}

	if (!role) {
		// only channel exists, no connection, the role cannot be determined now. it should be known few milliseconds later
		return null;
	}

	let status;

	/**
	 * determine if the exten is connected to some other side (an application or other exten) and should be
	 * seen as connected (not as a dialing or incoming call)
	 */
	let connected;
	if (channel.state == 'connected') {
		// for the exten to be connected, this must be fulfilled: 
		connected = connections[0] && ( // a connection must exist
			// and either there is a queue, it's parked (some dial application is connected)
			// or the connection itself is marked as connected (start_ts)
			
			
			// or there is no other channel = connected to some app, not queue or parking lot (or just connected to a queue before it's known as a queue)
			connections[0].conn.queue || 
			connections[0].conn.parked || 
			connections[0].conn.start_ts
		);
		// the dialer previously considered the connected state just from the channel.state. The real connection
		// however starts after connecting to the app or creating a bridge (which is when start_ts is set)
		
		// TODO: is this ok?
		//  this changes behavior for (blind and attended) transfers, where the transferred call is now considered not
		//  connected when dialing the transferee
	} else {
		connected = false;
	}
	
	if (!connected) {
		// either dialing or incoming
		if (role == 'caller') {
			status = 'dialing';
		} else {
			status = 'incomming';
		}
	} else {
		// connected channel
		if (channel.hold_start_ts) {
			status = 'hold';
		} else if (channel.musichold) {
			status = 'musichold';
		} else {
			status = 'connected';
		}
	}


	let peer;
	if (originate_dialing_caller) {
		// channel might not have the originate_target set, it's not inheritable variable (prefixed with __)
		// -> check the connection, too
		peer = channel.originate_target || connections[0] && connections[0].conn.originate_target;
	} else {
		// take the peer from the connected_line of the channel
		peer = channel.connected_line;
	}


	// handle attended transfer
	let transfer;
	
	// first, collect all connections where is the channel
	// we need collect the connection by the channel, not only by the peer (to show transfer to extens in followme chain)	
	const channel_connections = call_connections_sorted.map(conn => {
			if (conn.channel_caller?.channel == channel.channel) {
				return {conn, my_channel: channel, peer_channel: conn.channel_callee};
			}
			if (conn.channel_callee?.channel == channel.channel) {
				return {conn, my_channel: channel, peer_channel: conn.channel_caller};
			}
		}).filter(c => c);

	if ((channel_connections.length > 1 || channel.atxfer) && channel.hold_start_ts) {
		
		if (channel_connections.length > 0) {
			// peer must be overridden to the original connection other side, but only if there is a connection
			peer = channel_connections[0].peer_channel.caller;
		}

		if (channel_connections.length > 1) {
			if (channel_connections[1].peer_channel && channel_connections[1].peer_channel.state == 'connected') {
				transfer = {
					status: 'connected',
					peer: channel_connections[1].my_channel.connected_line,
				};
			} else {
				transfer = {
					status: 'dialing',
					// the queue is preferred here even if the dialing peer is known
					peer: channel_connections[1].conn.queue || channel.connected_line,
				}
			}
		} else {
			// early state, peer not yet known
			transfer = {
				status: 'dialing',  
				peer: null,
			}
		}
	}

	// strip queue prefix from peer if set
	if (connections[0] && connections[0].conn.queue_prefix && peer.startsWith(connections[0].conn.queue_prefix)) {
		peer = peer.substring(connections[0].conn.queue_prefix.length);
	}
	
	let early_ts;
	if (channel && channel.state != 'connected') {
		// the first time of our channel. Or conn.dial_ts if there is no channel (the call is originated in caller dial phase)
		early_ts = channel.first_time || connections[0].conn.dial_ts;
	}

	// get the first DID in any channel
	const did = call.peers
		.sort((a, b) => a.first_time.localeCompare(b.first_time))
		.map(c => c && c.did)
		.find(v => v);
	

	// collect all computed properties and return
	return {
		call_id: call.call_id,
		status,
		peer,

		// if the queue was not found in channels, try connections. The
		// queue is set only for connection (amd not channels) for
		// outbound calls, where the queue is set with prefix and
		// stripped in dialplan
		queue: (channel && channel.queue) || connections.map(c => c.conn.queue)[0] || undefined,

		early_ts,

		start_ts: connections[0] && connections[0].conn.start_ts,
		hold_start_ts: status == 'hold' ? channel.hold_start_ts : undefined,
		transfer,
		chanspy_spied: (connections[0] && connections[0].conn.chanspy_spied_exten) || undefined,
		playing_recording: channel && channel.playing_recording,

		// list of the exten channels. Stripped to just it's name
		// currently used only in web dialer to determine if there is another webRTC channel active
		channels: channels.map(c => c.channel),

		did,

		debug: {
			dbc: call
		},
	};
}
